import React, { useState, useEffect } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const ProjectForm = ({ project, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    title: '',
    stakeholder: '',
    department: 'Marketing',
    delivery_date: '',
    edit_start_date: '',
    final_edit_date: '',
    version: 'v1.0',
    type: 'Internal',
    urgency: 'Medium',
    status: 'Waiting',
    description: '',
    review_link: '',
    file_link: '',
  });

  useEffect(() => {
    if (project) {
      setFormData({
        ...project,
        delivery_date: project.delivery_date ? project.delivery_date.split('T')[0] : '',
        edit_start_date: project.edit_start_date ? project.edit_start_date.split('T')[0] : '',
        final_edit_date: project.final_edit_date ? project.final_edit_date.split('T')[0] : '',
      });
    }
  }, [project]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Convert dates back to ISO strings for storage
    const payload = {
      ...formData,
      delivery_date: formData.delivery_date ? new Date(formData.delivery_date).toISOString() : null,
      edit_start_date: formData.edit_start_date ? new Date(formData.edit_start_date).toISOString() : null,
      final_edit_date: formData.final_edit_date ? new Date(formData.final_edit_date).toISOString() : null,
    };

    // Remove internal Supabase fields if editing
    if (project) {
      delete payload.id;
      delete payload.created_at;
      delete payload.user_id;
    }

    onSave(payload);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 overflow-y-auto">
      <div className="bg-white dark:bg-gray-800 w-full max-w-2xl rounded-2xl shadow-2xl flex flex-col max-h-[90vh]">
        <div className="p-6 border-b dark:border-gray-700 flex justify-between items-center bg-gray-50 dark:bg-gray-900 rounded-t-2xl">
          <h2 className="text-xl font-bold dark:text-white">
            {project ? 'Edit Project' : 'New Project'}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-full dark:text-white">
            <SafeIcon icon={FiIcons.FiX} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 overflow-y-auto space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Project Title</label>
              <input 
                type="text" 
                name="title" 
                required 
                value={formData.title} 
                onChange={handleChange}
                className="w-full px-4 py-2 rounded-lg border dark:border-gray-600 bg-white dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-indigo-500 outline-none" 
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Stakeholder</label>
              <input 
                type="text" 
                name="stakeholder" 
                value={formData.stakeholder} 
                onChange={handleChange}
                className="w-full px-4 py-2 rounded-lg border dark:border-gray-600 bg-white dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-indigo-500 outline-none" 
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Area of Business</label>
              <select 
                name="department" 
                value={formData.department} 
                onChange={handleChange}
                className="w-full px-4 py-2 rounded-lg border dark:border-gray-600 bg-white dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-indigo-500 outline-none"
              >
                <option>Marketing</option>
                <option>Sales</option>
                <option>HR</option>
                <option>Product</option>
                <option>Leadership</option>
                <option>R&A</option>
                <option>ExCo</option>
                <option>Eco Teams</option>
                <option>Internal Comms</option>
                <option>External Comms</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Type</label>
              <select 
                name="type" 
                value={formData.type} 
                onChange={handleChange}
                className="w-full px-4 py-2 rounded-lg border dark:border-gray-600 bg-white dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-indigo-500 outline-none"
              >
                <option>Internal</option>
                <option>External</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Urgency</label>
              <select
                name="urgency"
                value={formData.urgency}
                onChange={handleChange}
                className="w-full px-4 py-2 rounded-lg border dark:border-gray-600 bg-white dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-indigo-500 outline-none"
              >
                <option>Low</option>
                <option>Medium</option>
                <option>High</option>
                <option>Critical</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Version</label>
              <input
                type="text"
                name="version"
                value={formData.version}
                onChange={handleChange}
                placeholder="e.g. v1.0"
                className="w-full px-4 py-2 rounded-lg border dark:border-gray-600 bg-white dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-indigo-500 outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Status</label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                className="w-full px-4 py-2 rounded-lg border dark:border-gray-600 bg-white dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-indigo-500 outline-none"
              >
                <option>Waiting</option>
                <option>Editing</option>
                <option>Sent for review</option>
                <option>Complete</option>
              </select>
            </div>

            <div className="md:col-span-2 grid grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Edit Start</label>
                <input 
                  type="date" 
                  name="edit_start_date" 
                  value={formData.edit_start_date} 
                  onChange={handleChange}
                  className="w-full px-3 py-2 text-sm rounded-lg border dark:border-gray-600 bg-white dark:bg-gray-700 dark:text-white" 
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Final Edit</label>
                <input 
                  type="date" 
                  name="final_edit_date" 
                  value={formData.final_edit_date} 
                  onChange={handleChange}
                  className="w-full px-3 py-2 text-sm rounded-lg border dark:border-gray-600 bg-white dark:bg-gray-700 dark:text-white" 
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Delivery</label>
                <input 
                  type="date" 
                  name="delivery_date" 
                  value={formData.delivery_date} 
                  onChange={handleChange}
                  className="w-full px-3 py-2 text-sm rounded-lg border dark:border-gray-600 bg-white dark:bg-gray-700 dark:text-white" 
                />
              </div>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Links</label>
              <div className="grid grid-cols-2 gap-4">
                <input 
                  placeholder="Review Link (e.g. Vimeo)" 
                  name="review_link" 
                  value={formData.review_link} 
                  onChange={handleChange}
                  className="w-full px-4 py-2 rounded-lg border dark:border-gray-600 bg-white dark:bg-gray-700 dark:text-white text-sm" 
                />
                <input 
                  placeholder="File Link (e.g. Dropbox)" 
                  name="file_link" 
                  value={formData.file_link} 
                  onChange={handleChange}
                  className="w-full px-4 py-2 rounded-lg border dark:border-gray-600 bg-white dark:bg-gray-700 dark:text-white text-sm" 
                />
              </div>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Notes</label>
              <textarea 
                name="description" 
                rows="3" 
                value={formData.description} 
                onChange={handleChange}
                className="w-full px-4 py-2 rounded-lg border dark:border-gray-600 bg-white dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-indigo-500 outline-none"
              ></textarea>
            </div>
          </div>
        </form>

        <div className="p-4 border-t dark:border-gray-700 flex justify-end gap-3 bg-gray-50 dark:bg-gray-900 rounded-b-2xl">
          <button onClick={onClose} className="px-5 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition">Cancel</button>
          <button onClick={handleSubmit} className="px-5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition font-medium">Save Project</button>
        </div>
      </div>
    </div>
  );
};

export default ProjectForm;